import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface OutstandingInvoice {
  id: string
  invoiceNumber: string
  invoiceDate: string
  dueDate: string
  clientName: string
  total: string
  paidAmount: string
  outstandingAmount: string
  currency: string
  status: string
}

interface OutstandingExpense {
  id: string
  expenseNumber: string
  expenseDate: string
  vendor: string | null
  description: string
  total: string
  paidAmount: string
  outstandingAmount: string
  currency: string
  status: string
}

interface UnallocatedPayment {
  id: string
  paymentNumber: string
  paymentDate: string
  amount: string
  currency: string
  status: string
  reference: string | null
  description: string | null
}

interface ReconciliationSummary {
  totalOutstandingInvoices: number
  totalOutstandingInvoiceAmount: string
  totalOutstandingExpenses: number
  totalOutstandingExpenseAmount: string
  totalUnallocatedPayments: number
  totalUnallocatedPaymentAmount: string
}

const PaymentReconciliation: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [summary, setSummary] = useState<ReconciliationSummary | null>(null)
  const [outstandingInvoices, setOutstandingInvoices] = useState<OutstandingInvoice[]>([])
  const [outstandingExpenses, setOutstandingExpenses] = useState<OutstandingExpense[]>([])
  const [unallocatedPayments, setUnallocatedPayments] = useState<UnallocatedPayment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'summary' | 'invoices' | 'expenses' | 'payments'>('summary')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setIsLoading(true)
      const [summaryRes, invoicesRes, expensesRes, paymentsRes] = await Promise.all([
        axios.get('/api/erp/payment-reconciliation/summary'),
        axios.get('/api/erp/payment-reconciliation/outstanding-invoices'),
        axios.get('/api/erp/payment-reconciliation/outstanding-expenses'),
        axios.get('/api/erp/payment-reconciliation/unallocated-payments'),
      ])

      setSummary(summaryRes.data.data)
      setOutstandingInvoices(invoicesRes.data.data || [])
      setOutstandingExpenses(expensesRes.data.data || [])
      setUnallocatedPayments(paymentsRes.data.data || [])
    } catch (error: any) {
      console.error('Error fetching reconciliation data:', error)
      toast.error('Failed to load reconciliation data')
    } finally {
      setIsLoading(false)
    }
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>
  }

  if (!hasPermission('erp_payment', 'read')) {
    return (
      <div className="p-6">
        <p className="text-red-800">You don't have permission to view payment reconciliation.</p>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Payment Reconciliation</h1>
          <PageHelp pageIdentifier="payment-reconciliation" title="Payment Reconciliation" />
        </div>
        <button
          onClick={fetchData}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('summary')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'summary'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Summary
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'invoices'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Outstanding Invoices ({outstandingInvoices.length})
          </button>
          <button
            onClick={() => setActiveTab('expenses')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'expenses'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Outstanding Expenses ({outstandingExpenses.length})
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'payments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Unallocated Payments ({unallocatedPayments.length})
          </button>
        </nav>
      </div>

      {isLoading ? (
        <p className="text-gray-600">Loading...</p>
      ) : (
        <>
          {activeTab === 'summary' && summary && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Outstanding Invoices</h3>
                <p className="text-3xl font-bold text-blue-600">{summary.totalOutstandingInvoices}</p>
                <p className="text-sm text-gray-600 mt-2">
                  {parseFloat(summary.totalOutstandingInvoiceAmount).toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                  })}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Outstanding Expenses</h3>
                <p className="text-3xl font-bold text-orange-600">{summary.totalOutstandingExpenses}</p>
                <p className="text-sm text-gray-600 mt-2">
                  {parseFloat(summary.totalOutstandingExpenseAmount).toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                  })}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Unallocated Payments</h3>
                <p className="text-3xl font-bold text-green-600">{summary.totalUnallocatedPayments}</p>
                <p className="text-sm text-gray-600 mt-2">
                  {parseFloat(summary.totalUnallocatedPaymentAmount).toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                  })}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {outstandingInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invoice.invoiceNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.clientName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(invoice.dueDate).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {parseFloat(invoice.total).toLocaleString('en-GB', { style: 'currency', currency: invoice.currency })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {parseFloat(invoice.paidAmount).toLocaleString('en-GB', { style: 'currency', currency: invoice.currency })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600 text-right">
                        {parseFloat(invoice.outstandingAmount).toLocaleString('en-GB', { style: 'currency', currency: invoice.currency })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'expenses' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expense #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {outstandingExpenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{expense.expenseNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{expense.vendor || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(expense.expenseDate).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {parseFloat(expense.total).toLocaleString('en-GB', { style: 'currency', currency: expense.currency })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {parseFloat(expense.paidAmount).toLocaleString('en-GB', { style: 'currency', currency: expense.currency })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600 text-right">
                        {parseFloat(expense.outstandingAmount).toLocaleString('en-GB', { style: 'currency', currency: expense.currency })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {unallocatedPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{payment.paymentNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(payment.paymentDate).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{payment.reference || payment.description || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600 text-right">
                        {parseFloat(payment.amount).toLocaleString('en-GB', { style: 'currency', currency: payment.currency })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default PaymentReconciliation
